import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';

import { CmsCollection, CmsTaxonomyGeneral, CmsTeaserCard } from '@fcom/core-api/interfaces/cms-interface';
import { isCmsImageData } from '@fcom/common/components/images/images.utils';
import { CONTEXT, Schema, SchemaService, SchemaTypes } from '@fcom/common/services/schema/schema.service';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { toISO8601UTC } from '@fcom/core/utils/date';
import { getWidestImageWidth } from '@fcom/ui-components/components/images/utils';

const BLUEWINGS_ARTICLE_LIST_SCHEMA_KEY = 'ItemList:bluewings';

const toAbsoluteUrl = (url: string, origin: string): string => {
  return new URL(url, origin).href;
};

@Component({
  selector: 'cms-ond-bluewings',
  templateUrl: 'cms-ond-bluewings.component.html',
  styleUrls: ['cms-ond-bluewings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsOndBluewingsComponent {
  private readonly schemaService = inject(SchemaService);
  private readonly configService = inject(ConfigService);

  readonly content = input.required<CmsCollection<CmsTeaserCard>>();

  readonly stories = computed<CmsTeaserCard[]>(() => this.content().items ?? []);

  readonly title = computed<string | undefined>(() => {
    const content = this.content();
    const hasNoTitleTag = (content.subjectTaxonomyTags ?? []).includes(CmsTaxonomyGeneral.NO_TITLE);

    return hasNoTitleTag ? undefined : content.teaserTitle;
  });

  readonly schema = computed<Schema | null>(() => {
    const { baseUrl, cmsUrl } = this.configService.cfg;
    const stories = this.stories().filter((story) => {
      return !!story.url;
    });

    if (!stories.length) {
      return null;
    }

    return {
      '@context': CONTEXT,
      '@type': SchemaTypes.ITEM_LIST,
      name: this.content().teaserTitle || undefined,
      numberOfItems: stories.length,
      itemListElement: stories.map((story, index): Schema => {
        const images = story.media?.find(isCmsImageData)?.responsiveImageLinksData?.landscape_ratio16x9 ?? {};
        const image = images[getWidestImageWidth(images)];
        const url = toAbsoluteUrl(story.url, baseUrl);

        return {
          '@type': SchemaTypes.LIST_ITEM,
          position: index + 1,
          item: {
            '@type': SchemaTypes.BLOG,
            '@id': url,
            url,
            headline: story.teaserTitle || undefined,
            image: image ? toAbsoluteUrl(image, cmsUrl) : undefined,
            datePublished: story.externallyDisplayedDate
              ? toISO8601UTC(new Date(story.externallyDisplayedDate))
              : undefined,
            inLanguage: story.contentLocale?.replace('_', '-') || undefined,
          },
        };
      }),
    };
  });

  constructor() {
    effect((onCleanup) => {
      const schema = this.schema();
      if (!schema) {
        return;
      }

      this.schemaService.setSchema(BLUEWINGS_ARTICLE_LIST_SCHEMA_KEY, schema);
      onCleanup(() => {
        this.schemaService.removeSchema(BLUEWINGS_ARTICLE_LIST_SCHEMA_KEY);
      });
    });
  }
}
